import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Modal from '../../shared/Modal'
import Select from '../../ui/Select/Select'
import Loader from '../../shared/Loader'
import Button from '../../ui/Button'
import LinkButton from '../../ui/LinkButton'
import {
  DEFAULT_COLUMN_PREFERENCES,
  CUSTOMIZABLE_PRIMARY_COLUMN_OPTIONS,
  PRIMARY_COLUMN_KEYS,
  MAX_EXTRA_COLUMNS
} from './constants'
import ModalHeader from '../shared/ModalHeader'
import {
  MODAL_BODY_CLASS, MODAL_FIELD_CLASS,
  MODAL_FOOTER_SPLIT_CLASS, MODAL_FOOTER_ACTIONS_CLASS, MODAL_CANCEL_BTN_CLASS
} from '../shared/styles'

const SECTION_TITLE_CLASS = 'tw:text-base! tw:font-medium! tw:text-gray-700! tw:m-0!'
const SECTION_HINT_CLASS = 'tw:text-sm tw:text-gray-700 tw:m-0!'
const RESET_LINK_OVERRIDE = 'tw:text-gray-900! tw:hover:text-gray-900! tw:active:text-gray-900!'
const EMPTY_CLASS = 'tw:text-sm tw:text-gray-500 tw:italic'
const HELP_CLASS = 'tw:text-xs tw:text-gray-500 tw:mt-1'
const DIVIDER_CLASS = 'tw:w-full tw:m-0! tw:border-t! tw:border-solid! tw:border-gray-300!'

const usePreviousOpenState = isOpen => {
  const [wasOpen, setWasOpen] = useState(isOpen)
  useEffect(() => setWasOpen(isOpen), [isOpen])
  return wasOpen
}

const EMPTY_LIST = []

const ColumnSettingsModal = ({
  isOpen, onClose, preferences, onApply, onReset,
  questionSections = EMPTY_LIST, variables = EMPTY_LIST, optionsLoading = false
}) => {
  const wasOpen = usePreviousOpenState(isOpen)
  const [localPreferences, setLocalPreferences] = useState(preferences)

  // Reset local state every time the modal opens so a previous cancel does not leak.
  useEffect(() => {
    if (isOpen && !wasOpen)
      setLocalPreferences(preferences)
  }, [isOpen, wasOpen, preferences])

  const questionToOption = question => ({ value: question.id, label: question.title })
  const variableToOption = variable => ({ value: variable.id, label: variable.name })

  // Preserve the user's pick order: resolve options by walking the selected ids
  // Array (the source of truth) and looking them up in the option list. Filtering
  // The list would yield alphabetical order instead.
  const valuesInOrder = (ids, options) => {
    const lookup = new Map(options.map(option => [option.value, option]))
    return ids.map(id => lookup.get(id)).filter(Boolean)
  }

  const groupedQuestionOptions = useMemo(() => questionSections.map(section => ({
    label: section.name,
    options: section.questions.map(questionToOption)
  })), [questionSections])
  // Flat list needed for the order-preserving lookup of selected ids.
  const flatQuestionOptions = useMemo(() =>
    groupedQuestionOptions.flatMap(group => group.options), [groupedQuestionOptions])
  const variableOptions = useMemo(() => variables.map(variableToOption), [variables])
  const customizablePrimary = localPreferences.primaryColumns.filter(
    key => key !== PRIMARY_COLUMN_KEYS.ID
  )
  const primaryValue = valuesInOrder(customizablePrimary, CUSTOMIZABLE_PRIMARY_COLUMN_OPTIONS)
  const questionValue = valuesInOrder(localPreferences.questionIds, flatQuestionOptions)
  const variableValue = valuesInOrder(localPreferences.variableIds, variableOptions)

  const handlePrimaryChange = useCallback(selected => {
    const values = (selected || []).map(option => option.value)
    // ID is mandatory and not part of the picker; keep it pinned.
    setLocalPreferences(prev => ({ ...prev, primaryColumns: [PRIMARY_COLUMN_KEYS.ID, ...values] }))
  }, [])

  // Cap is global to both extra lists: a new pick is rejected if it would
  // Push the combined size over MAX_EXTRA_COLUMNS.
  const setListIfWithinCap = useCallback((key, otherKey, selected) => {
    const values = (selected || []).map(option => option.value)
    setLocalPreferences(prev => {
      if (values.length + prev[otherKey].length > MAX_EXTRA_COLUMNS)
        return prev
      return { ...prev, [key]: values }
    })
  }, [])

  const handleQuestionChange = useCallback(selected => {
    setListIfWithinCap('questionIds', 'variableIds', selected)
  }, [setListIfWithinCap])
  const handleVariableChange = useCallback(selected => {
    setListIfWithinCap('variableIds', 'questionIds', selected)
  }, [setListIfWithinCap])

  const [resetPending, setResetPending] = useState(false)

  const handleReset = useCallback(() => {
    setLocalPreferences(DEFAULT_COLUMN_PREFERENCES)
    setResetPending(true)
  }, [])

  // Clear staged reset on reopen so a previously cancelled reset is not applied later.
  useEffect(() => {
    if (isOpen && !wasOpen)
      setResetPending(false)
  }, [isOpen, wasOpen])

  const handleApply = useCallback(() => {
    if (resetPending)
      onReset()
    onApply(localPreferences)
    onClose()
  }, [localPreferences, onApply, onClose, onReset, resetPending])

  const extraCount = localPreferences.questionIds.length + localPreferences.variableIds.length
  const extraLimitReached = extraCount >= MAX_EXTRA_COLUMNS

  const renderLoader = () => (
    <div className="tw:py-3 tw:flex tw:justify-center">
      <Loader />
    </div>
  )

  const renderLimitHint = () => {
    if (!extraLimitReached)
      return null
    return (
      <p className={HELP_CLASS}>
        Limite atteinte : 10 colonnes supplémentaires maximum (questions + variables).
      </p>
    )
  }

  const renderExtraSection = () => {
    if (flatQuestionOptions.length === 0)
      return <p className={EMPTY_CLASS}>Aucune question disponible</p>
    return (
      <Select
        closeMenuOnSelect={false}
        isClearable={false}
        menuPortalTarget={document.body}
        options={groupedQuestionOptions}
        placeholder="Sélectionner..."
        value={questionValue}
        isMulti
        onChange={handleQuestionChange}
      />
    )
  }

  const renderVariableSection = () => {
    if (variableOptions.length === 0)
      return <p className={EMPTY_CLASS}>Aucune variable disponible sur l’ensemble</p>
    return (
      <Select
        closeMenuOnSelect={false}
        isClearable={false}
        menuPortalTarget={document.body}
        options={variableOptions}
        placeholder="Sélectionner..."
        value={variableValue}
        isMulti
        onChange={handleVariableChange}
      />
    )
  }

  const renderExtraFields = () => {
    if (optionsLoading)
      return renderLoader()
    return (
      <>
        <div className={MODAL_FIELD_CLASS}>
          <h6 className={SECTION_TITLE_CLASS}>Colonnes supplémentaires</h6>
          <p className={SECTION_HINT_CLASS}>
            Vous pouvez ajouter jusqu’à 10 colonnes supplémentaires.
          </p>
          {renderExtraSection()}
        </div>
        <hr className={DIVIDER_CLASS} />
        <div className={MODAL_FIELD_CLASS}>
          <h6 className={SECTION_TITLE_CLASS}>
            Contenu des colonnes
            {' '}
            <span className="tw:italic">Variables</span>
          </h6>
          <p className={SECTION_HINT_CLASS}>
            Choisissez quelles variables doivent être affichées dans ces colonnes.
          </p>
          {renderVariableSection()}
          {renderLimitHint()}
        </div>
      </>
    )
  }

  const renderHeader = () =>
    <ModalHeader title="Personnaliser le tableau" onClose={onClose} />

  const renderFooter = () => (
    <div className={MODAL_FOOTER_SPLIT_CLASS}>
      <LinkButton className={RESET_LINK_OVERRIDE} onClick={handleReset}>
        Réinitialiser l’affichage
      </LinkButton>
      <div className={MODAL_FOOTER_ACTIONS_CLASS}>
        <Button className={MODAL_CANCEL_BTN_CLASS} variant="neutral" onClick={onClose}>
          Annuler
        </Button>
        <Button variant="primary" onClick={handleApply}>
          Appliquer
        </Button>
      </div>
    </div>
  )

  return (
    <Modal
      footer={renderFooter()}
      header={renderHeader()}
      isOpen={isOpen}
      title="Personnaliser le tableau"
      onClose={onClose}
    >
      <div className={MODAL_BODY_CLASS}>
        <div className={MODAL_FIELD_CLASS}>
          <h6 className={SECTION_TITLE_CLASS}>Colonnes principales</h6>
          <span className={SECTION_HINT_CLASS}>
            Par défaut, toutes les colonnes sont affichées dans le tableau.
            Masquez celles qui ne vous sont pas utiles et ajoutez, si besoin,
            des colonnes de questions ou de variables. La colonne ID reste
            toujours affichée.
          </span>
          <Select
            closeMenuOnSelect={false}
            isClearable={false}
            menuPortalTarget={document.body}
            options={CUSTOMIZABLE_PRIMARY_COLUMN_OPTIONS}
            placeholder="Sélectionner les colonnes..."
            value={primaryValue}
            isMulti
            onChange={handlePrimaryChange}
          />
        </div>
        {renderExtraFields()}
      </div>
    </Modal>
  )
}

ColumnSettingsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  optionsLoading: PropTypes.bool,
  preferences: PropTypes.shape({
    primaryColumns: PropTypes.arrayOf(PropTypes.string),
    questionIds: PropTypes.arrayOf(PropTypes.number),
    variableIds: PropTypes.arrayOf(PropTypes.number)
  }).isRequired,
  questionSections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    questions: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired
    })).isRequired
  })),
  variables: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  }))
}

ColumnSettingsModal.defaultProps = {
  optionsLoading: false,
  questionSections: [],
  variables: []
}

export default ColumnSettingsModal
